import React, { createContext, useContext, useEffect, useMemo } from 'react'
import * as API from '../api'
import { ShopifyCustomerAPIOptions } from '../api'
import assert from 'assert'
import { useSearchParams } from 'react-router-dom'

type APIContextType = {
  initialized: boolean
  hasSession: boolean
  customer?: API.ShopifyCustomerAPI
  shopDomain?: string
  email?: string
}

const APIContext = createContext<APIContextType>({
  initialized: false,
  hasSession: false,
  customer: undefined,
  shopDomain: undefined,
  email: undefined,
})

const apiConfiguration = (): ShopifyCustomerAPIOptions => {
  const shopDomain = window.verisartAppConfig?.shopDomain
  const customerId = window.verisartAppConfig?.customerId
  const customerSecret = window.verisartAppConfig?.customerSecret

  assert(shopDomain, 'shopDomain is required')

  return {
    shopDomain,
    customerId,
    customerSecret,
    baseUrl: process.env.REACT_APP_VERISART_API_URL ?? '',
  }
}

export const APIProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [initialized, setInitialized] = React.useState(false)
  const [hasSession, setHasSession] = React.useState(false)
  const [email, setEmail] = React.useState<string | undefined>(undefined)
  const config = useMemo(() => apiConfiguration(), [])

  useEffect(() => {
    const init = async () => {
      // If store not minting activated redirect to homepage
      if (
        process.env.NODE_ENV === 'production' &&
        window.verisartAppConfig?.dashboardDisabled === 'true'
      ) {
        window.location.href = '/'
        return
      }

      // This is needed because we're changing the search params via setSearchParams().
      // Doing so would cause a re-running of this useEffect() because of our dependency on
      // searchParams. We use this state to skip the second render
      if (initialized) {
        return
      }

      assert(config.shopDomain, 'shopDomain is required')
      API.initializeShopifyCustomerAPI(config)

      // Register to be notified about session expiry
      API.customer.registerSessionStatusCallback(setHasSession)

      // Get the auth code (note we remove it from the URL for security reasons (e.g. prevent a user accidentally copying and pasting))
      const authCode = searchParams.get('authCode')
      searchParams.delete('authCode')
      const email = searchParams.get('email')
      searchParams.delete('email')
      if (email) {
        setEmail(decodeURIComponent(email))
      }
      setSearchParams(searchParams, { replace: true })
      // Try to refresh the token from the authCode query param
      if (authCode) {
        await API.customer.tryRefreshTokenFromMagicLinkCode(authCode)
      }

      // Fallback to refreshing from customer secret
      await API.customer.tryRefreshSessionFromCustomerSecret()

      setHasSession(API.customer.hasSession())

      try {
        // Workaround for VER-6985: The shopify admin bar (something Shopify injects into all pages when you
        // are logged in as the merchant of a store) seems to be broken on our customer dashboard pages
        // and appears at the top left, blocking real items. This is a hack to hide it.
        const element = document.getElementById('admin-bar-iframe')
        // Note we're being slightly defensive by only doing if `bottom: 0` is missing from the style.
        // This is based on the current implementation of the admin bar, but could change in the future.
        if (
          element &&
          !element.attributes['style']?.value?.includes('bottom: 0')
        ) {
          element.attributes['style'].value = 'display: none;'
        }
      } catch (_) {
        // Nothing
      }

      setInitialized(true)
    }
    init()
  }, [config, initialized, searchParams, setSearchParams])

  const value = {
    initialized,
    hasSession,
    config,
    customer: API.customer,
    shopDomain: config.shopDomain,
    email,
  }

  return <APIContext.Provider value={value}>{children}</APIContext.Provider>
}

export const useAPI = () => {
  const context = useContext(APIContext)
  if (context === undefined) {
    throw new Error('useAPI must be used within an APIProvider')
  }
  return context
}
